// Cliente HTTP do app de dispositivo (totem facial, recepção e sala).
//
// Rotas públicas: capabilities, salas do totem e observação facial.
// Rotas de dispositivo (5.8): exigem `Authorization: Bearer <sessão>` e, nas escritas,
// `Idempotency-Key`. Um 401 significa link revogado/expirado: a sessão é limpa e as telas
// são avisadas pelo evento `DEVICE_SESSION_EVENT` (ver utils/session.ts).
import type {
  CreateRegistrationRequest,
  DeathRequest,
  DeviceCapabilitiesResponse,
  DeviceSession,
  FacialObservationResponse,
  ListRegistrationsResponse,
  ListRoomDeviceRoomsResponse,
  ListTotemRoomsResponse,
  PatientRegistration,
  RegistrationResponse,
  RoomDeviceRoom,
  RoomDeviceRoomResponse,
  RoomStay,
  ScanRequest,
  ScanResponse,
  TestMessageResponse,
  UpdateRegistrationRequest,
  UrgencyResponse
} from "@centro-cirurgico/contracts";

import {
  clearDeviceSession,
  clearLinkToken,
  readDeviceSession,
  readStoredLinkToken,
  storeDeviceSession
} from "./session";

// Base URL da API:
//   - `VITE_API_BASE_URL`, quando definida no .env do modo de build;
//   - nos endereços *.pages.dev (sem rota /api) o totem chama o Worker direto pelo workers.dev;
//   - no domínio próprio (centrocirurgicocontrol.com e dev.centrocirurgicocontrol.com) a API
//     fica na mesma origem e a base é vazia, igual ao dev local (proxy do Vite para o wrangler dev).
const API_WORKER_URL_BY_PAGES_HOST: Record<string, string> = {
  "centro-cirurgico-control.pages.dev":
    "https://api-centro-cirurgico-control.centro-cirurgico-control.workers.dev",
  "centro-cirurgico-control-dev.pages.dev":
    "https://api-centro-cirurgico-control-dev.centro-cirurgico-control.workers.dev"
};

function resolveApiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, "");
  if (configured) return configured;

  if (typeof window !== "undefined") {
    const host = window.location.host;
    for (const [pagesHost, workerUrl] of Object.entries(API_WORKER_URL_BY_PAGES_HOST)) {
      if (host === pagesHost || host.endsWith(`.${pagesHost}`)) return workerUrl;
    }
  }

  return "";
}

const API_BASE_URL: string = resolveApiBaseUrl();

export interface ApiIssue {
  path?: string;
  message: string;
}

export class HttpError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: ApiIssue[];

  constructor(status: number, code: string, message: string, issues: ApiIssue[] = []) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

/** Mensagem legível para qualquer erro (HttpError usa o `title` da API). */
export function errorMessage(error: unknown, fallback = "Não foi possível concluir a operação."): string {
  if (error instanceof HttpError) return error.message || fallback;
  if (error instanceof TypeError) return "Sem conexão com o servidor. Verifique a rede.";
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType.includes("application/json") || contentType.includes("application/problem+json");
}

function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // Fallback (contextos sem Web Crypto): UUID v4 pseudoaleatório.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

async function parseError(response: Response, authenticated: boolean): Promise<HttpError> {
  const contentType = response.headers.get("content-type");
  let title = `Erro ${response.status}`;
  let code = "unknown";
  let issues: ApiIssue[] = [];
  if (isJsonContentType(contentType)) {
    const body = (await response.json().catch(() => null)) as
      | { title?: unknown; code?: unknown; issues?: unknown }
      | null;
    if (body) {
      if (typeof body.title === "string" && body.title) title = body.title;
      if (typeof body.code === "string" && body.code) code = body.code;
      if (Array.isArray(body.issues)) {
        issues = body.issues
          .filter((issue): issue is { message: string; path?: string } => {
            return !!issue && typeof issue === "object" && typeof (issue as { message?: unknown }).message === "string";
          })
          .map((issue) => (issue.path ? { path: issue.path, message: issue.message } : { message: issue.message }));
      }
    }
  }
  if (response.status === 401 && authenticated) {
    // Link revogado/expirado (a renovação já foi tentada): derruba a sessão local.
    clearLinkToken();
    clearDeviceSession("revoked");
    if (code === "unknown") code = "device_session_invalid";
    if (title === "Erro 401") title = "Link revogado ou expirado. Peça um novo link à coordenação.";
  }
  return new HttpError(response.status, code, title, issues);
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** Envia o bearer da sessão de dispositivo (obrigatório nas rotas de dispositivo). */
  auth?: boolean;
  signal?: AbortSignal | undefined;
}

/**
 * Requisição genérica à API. Lança `HttpError` em respostas não-2xx.
 * Escritas autenticadas recebem `Idempotency-Key` automaticamente.
 */
let renewing: Promise<DeviceSession | null> | null = null;

/**
 * Renova a sessão de dispositivo com o token do link guardado neste aparelho.
 * Uma renovação por vez; `null` se não houver link ou se ele foi revogado/expirou.
 */
export function renewDeviceSession(): Promise<DeviceSession | null> {
  const linkToken = readStoredLinkToken();
  if (!linkToken) return Promise.resolve(null);
  renewing ??= fetch(`${API_BASE_URL}/api/v1/access/exchange`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token: linkToken })
  })
    .then(async (response) => {
      if (response.ok) {
        const session = (await response.json()) as DeviceSession;
        storeDeviceSession(session);
        return session;
      }
      if (response.status === 401 || response.status === 404 || response.status === 410) {
        clearLinkToken();
        clearDeviceSession("revoked");
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      renewing = null;
    });
  return renewing;
}

export async function request<T>(path: string, options: RequestOptions = {}, retried = false): Promise<T> {
  const method = options.method ?? "GET";
  const auth = options.auth ?? false;
  const headers = new Headers({ Accept: "application/json" });

  if (auth) {
    const session = readDeviceSession() ?? (await renewDeviceSession());
    if (!session) {
      clearDeviceSession("missing");
      throw new HttpError(401, "device_session_missing", "Sem sessão de dispositivo. Abra o link de acesso.");
    }
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  let body: string | undefined;
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  if (method !== "GET") headers.set("Idempotency-Key", newIdempotencyKey());

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = body;
  if (options.signal) init.signal = options.signal;

  const response = await fetch(`${API_BASE_URL}${path}`, init);

  // Sessão vencida: renova pelo link guardado e repete a chamada uma vez.
  if (response.status === 401 && auth && !retried && readStoredLinkToken()) {
    const renewed = await renewDeviceSession();
    if (renewed) return request<T>(path, options, true);
  }

  if (!response.ok) throw await parseError(response, auth);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type");
  if (!isJsonContentType(contentType)) {
    throw new HttpError(
      response.status,
      "invalid_content_type",
      `Resposta inesperada do servidor (esperava JSON, recebeu ${contentType || "desconhecido"}). ` +
        "Possível causa: URL da API não configurada corretamente."
    );
  }
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Rotas públicas (totem facial)
// ---------------------------------------------------------------------------

export function getDeviceCapabilities(): Promise<DeviceCapabilitiesResponse> {
  return request<DeviceCapabilitiesResponse>("/api/v1/device/capabilities");
}

export function getTotemRooms(): Promise<ListTotemRoomsResponse> {
  return request<ListTotemRoomsResponse>("/api/v1/totem/rooms");
}

type FaceObservationListener = (response: FacialObservationResponse) => void;
const faceObservationListeners = new Set<FaceObservationListener>();

/**
 * Permite que a tela do totem observe TODAS as respostas do reconhecimento (inclusive
 * `blocked`, que o hook facial descarta) sem alterar `facial/**`.
 */
export function subscribeFaceObservation(listener: FaceObservationListener): () => void {
  faceObservationListeners.add(listener);
  return () => {
    faceObservationListeners.delete(listener);
  };
}

/** URL absoluta de um caminho da API (ex.: foto de referência assinada devolvida pelo totem). */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export async function observeFace(input: {
  roomId: string;
  imageBase64: string;
  exitToken?: string;
}): Promise<FacialObservationResponse> {
  const response = await request<FacialObservationResponse>("/api/v1/totem/facial/observe", {
    method: "POST",
    body: input
  });
  faceObservationListeners.forEach((listener) => {
    try {
      listener(response);
    } catch {
      // Um ouvinte com erro não pode derrubar o fluxo do totem.
    }
  });
  return response;
}

// ---------------------------------------------------------------------------
// Link de acesso (5.4)
// ---------------------------------------------------------------------------

export function exchangeAccessToken(token: string): Promise<DeviceSession> {
  return request<DeviceSession>("/api/v1/access/exchange", { method: "POST", body: { token } });
}

// ---------------------------------------------------------------------------
// Recepção (modo `reception`)
// ---------------------------------------------------------------------------

export async function listRegistrations(date: string): Promise<PatientRegistration[]> {
  const response = await request<ListRegistrationsResponse>(
    `/api/v1/reception/registrations?date=${encodeURIComponent(date)}`,
    { auth: true }
  );
  return response.registrations;
}

/** Cadastro ativo pela pulseira; `null` quando não existe (404). */
export async function getRegistrationByCode(code: string): Promise<PatientRegistration | null> {
  try {
    const response = await request<RegistrationResponse>(
      `/api/v1/reception/registrations/by-code/${encodeURIComponent(code)}`,
      { auth: true }
    );
    return response.registration;
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) return null;
    throw error;
  }
}

export async function createRegistration(input: CreateRegistrationRequest): Promise<PatientRegistration> {
  const response = await request<RegistrationResponse>("/api/v1/reception/registrations", {
    method: "POST",
    body: input,
    auth: true
  });
  return response.registration;
}

export async function updateRegistration(
  id: string,
  input: UpdateRegistrationRequest
): Promise<PatientRegistration> {
  const response = await request<RegistrationResponse>(
    `/api/v1/reception/registrations/${encodeURIComponent(id)}`,
    { method: "PATCH", body: input, auth: true }
  );
  return response.registration;
}

export function sendTestMessage(id: string): Promise<TestMessageResponse> {
  return request<TestMessageResponse>(
    `/api/v1/reception/registrations/${encodeURIComponent(id)}/test-message`,
    { method: "POST", auth: true }
  );
}

// ---------------------------------------------------------------------------
// Sala (modo `room`)
// ---------------------------------------------------------------------------

export async function listRoomDeviceRooms(): Promise<RoomDeviceRoom[]> {
  const response = await request<ListRoomDeviceRoomsResponse>("/api/v1/room-device/rooms", { auth: true });
  return response.rooms;
}

export async function getRoomDeviceRoom(roomId: string): Promise<RoomDeviceRoom> {
  const response = await request<RoomDeviceRoomResponse>(
    `/api/v1/room-device/rooms/${encodeURIComponent(roomId)}`,
    { auth: true }
  );
  return response.room;
}

export function scanWristband(roomId: string, input: ScanRequest): Promise<ScanResponse> {
  return request<ScanResponse>(`/api/v1/room-device/rooms/${encodeURIComponent(roomId)}/scan`, {
    method: "POST",
    body: input,
    auth: true
  });
}

export function confirmExit(stayId: string): Promise<{ stay: RoomStay; room: RoomDeviceRoom }> {
  return request<{ stay: RoomStay; room: RoomDeviceRoom }>(
    `/api/v1/room-device/stays/${encodeURIComponent(stayId)}/confirm-exit`,
    { method: "POST", auth: true }
  );
}

export function callCompanion(stayId: string): Promise<UrgencyResponse> {
  return request<UrgencyResponse>(`/api/v1/room-device/stays/${encodeURIComponent(stayId)}/urgency`, {
    method: "POST",
    auth: true
  });
}

export function registerDeath(stayId: string, input: DeathRequest): Promise<{ stay: RoomStay; room: RoomDeviceRoom }> {
  return request<{ stay: RoomStay; room: RoomDeviceRoom }>(
    `/api/v1/room-device/stays/${encodeURIComponent(stayId)}/death`,
    { method: "POST", body: input, auth: true }
  );
}

export async function finishSetup(roomId: string): Promise<RoomDeviceRoom> {
  const response = await request<RoomDeviceRoomResponse>(
    `/api/v1/room-device/rooms/${encodeURIComponent(roomId)}/setup/finish`,
    { method: "POST", auth: true }
  );
  return response.room;
}
